import { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  LAYER_DEFINITIONS,
  getLayerForObject as getLayerForObjectFromDefinitions,
} from '../constants/layerDefinitions';
import { getObjectTypeName } from '../config/gardenObjectTypes';
import { ensureObjectId } from '../components/Editor/tools/gateLinkUtils';
import { GardenSettingsContext } from './GardenSettingsContext';

/**
 * LayerContext - Manages layer visibility, lock state, and organization
 *
 * Features:
 * - Layer visibility toggle (eye icon)
 * - Layer lock toggle (lock icon)
 * - Collapsed/expanded state per layer (for UI)
 * - Object counting per layer
 * - Object display names for layer panel
 * - localStorage persistence
 */

const LayerContext = createContext(null);

// Default visibility: all layers visible
const DEFAULT_VISIBILITY = {
  background: true,
  outline: true,
  ground_cover: true,
  structures: true,
  beds: true,
  misc: true,
  plants: true,
};

// Default collapsed state: all layers collapsed (only summary shown)
const DEFAULT_COLLAPSED = {
  background: true,
  outline: true,
  ground_cover: true,
  structures: true,
  beds: true,
  misc: true,
  plants: true,
};

// Default lock state: all layers unlocked
const DEFAULT_LOCKED = {
  background: false,
  outline: false,
  ground_cover: false,
  structures: false,
  beds: false,
  misc: false,
  plants: false,
};

/**
 * Serialize the three layer view-state maps into the persisted
 * `settings.layers` form (Issue #466): one entry per fixed layer with
 * visible/locked/collapsed. name/color/order are intentionally omitted — they
 * stay read-only in LAYER_DEFINITIONS (no rename/reorder).
 */
const serializeLayerState = (visibility, locked, collapsed) =>
  LAYER_DEFINITIONS.map((layer) => ({
    id: layer.id,
    visible: visibility[layer.id] !== false,
    locked: locked[layer.id] === true,
    collapsed: collapsed[layer.id] !== false,
  }));

/**
 * Build the three view-state maps from a persisted `settings.layers` list,
 * starting from defaults so any missing/new layer keeps sane behavior.
 */
const deserializeLayerState = (layers) => {
  const visibility = { ...DEFAULT_VISIBILITY };
  const locked = { ...DEFAULT_LOCKED };
  const collapsed = { ...DEFAULT_COLLAPSED };
  layers.forEach((layer) => {
    if (!layer || typeof layer.id !== 'string') return;
    if (typeof layer.visible === 'boolean') visibility[layer.id] = layer.visible;
    if (typeof layer.locked === 'boolean') locked[layer.id] = layer.locked;
    if (typeof layer.collapsed === 'boolean') collapsed[layer.id] = layer.collapsed;
  });
  return { visibility, locked, collapsed };
};

// localStorage keys
const VISIBILITY_STORAGE_KEY = 'gartenplaner_layer_visibility';
const COLLAPSED_STORAGE_KEY = 'gartenplaner_layer_collapsed';
const LOCKED_STORAGE_KEY = 'gartenplaner_layer_locked';

export const LayerProvider = ({ children }) => {
  // Layer visibility state
  const [layerVisibility, setLayerVisibilityState] = useState(() => {
    try {
      const saved = localStorage.getItem(VISIBILITY_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...DEFAULT_VISIBILITY, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load layer visibility from localStorage:', error);
    }
    return DEFAULT_VISIBILITY;
  });

  // Layer lock state
  const [layerLocked, setLayerLockedState] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCKED_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...DEFAULT_LOCKED, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load layer locked state from localStorage:', error);
    }
    return DEFAULT_LOCKED;
  });

  // Layer collapsed state
  const [layerCollapsed, setLayerCollapsedState] = useState(() => {
    try {
      const saved = localStorage.getItem(COLLAPSED_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...DEFAULT_COLLAPSED, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load layer collapsed state from localStorage:', error);
    }
    return DEFAULT_COLLAPSED;
  });

  // Per-object hide/lock state (#760): a map from a stable object key to its
  // individual { hidden, locked } flags. Persisted per garden inside
  // settings.objectViewState (same #466 bridge as layers below) — the only home
  // that survives a reload for ALL object types, including the background,
  // outline and height zones, which are rebuilt from settings on each load and
  // never stored in canvas_objects. Living in the context (not the LayerPanel)
  // it also survives the Ebenen↔Eigenschaften tab switch that unmounts the panel.
  const [objectViewState, setObjectViewState] = useState({});

  // --- Backend persistence bridge (Issue #466) ---
  // Consume GardenSettingsContext null-safely: when LayerProvider is mounted
  // without a GardenSettingsProvider (e.g. isolated tests), gardenSettings is
  // null and the provider degrades to localStorage-only — exactly the previous
  // behavior. In the real app, LayerProvider sits inside GardenSettingsProvider.
  const gardenSettings = useContext(GardenSettingsContext);
  const persistedLayers = gardenSettings?.settings?.layers;
  const persistedObjectViewState = gardenSettings?.settings?.objectViewState;
  const updateSettings = gardenSettings?.updateSettings;
  const gardenId = gardenSettings?.gardenId;

  // Set true by user-driven mutation handlers (toggle/set/reset) and consumed
  // once by the write-back effect below. Hydration and mount never set it, so
  // loading a garden — or just rendering — never writes back. This is what
  // keeps hydration ↔ write-back from looping.
  const pendingPersistRef = useRef(false);

  // Hydrate the three maps from settings.layers when the active garden changes.
  // Uses React's "adjust state during render" pattern (not an effect) so the
  // new garden's layer state is applied before paint, in one render pass, and
  // without tripping set-state-in-effect lint. Keyed on gardenId:
  // loadFromBackend batches setGardenId + setAllSettings, so when gardenId
  // changes, settings.layers is already present in the same render. Legacy
  // gardens (no layers) keep the localStorage/default state. Write-backs change
  // settings.layers but not gardenId, so they never re-hydrate → no loop.
  const [hydratedGardenId, setHydratedGardenId] = useState(undefined);
  if (gardenId !== hydratedGardenId) {
    setHydratedGardenId(gardenId);
    if (Array.isArray(persistedLayers) && persistedLayers.length > 0) {
      const { visibility, locked, collapsed } = deserializeLayerState(persistedLayers);
      setLayerVisibilityState(visibility);
      setLayerLockedState(locked);
      setLayerCollapsedState(collapsed);
    }
    // Per-object view-state (#760): adopt the new garden's map, or reset to empty
    // so a previous garden's entries never leak into one that has none.
    setObjectViewState(
      persistedObjectViewState && typeof persistedObjectViewState === 'object'
        ? persistedObjectViewState
        : {}
    );
  }

  // Write the current view-state back to settings.layers (→ isDirty →
  // auto-save) after a user-driven change. Runs in an effect (not in the
  // handlers) so batched multi-toggles compose via functional setState and we
  // persist the final composed state exactly once. No-op without a
  // GardenSettingsProvider (localStorage-only fallback).
  useEffect(() => {
    if (!pendingPersistRef.current) return;
    pendingPersistRef.current = false;
    if (!updateSettings) return;
    updateSettings({
      layers: serializeLayerState(layerVisibility, layerLocked, layerCollapsed),
      objectViewState,
    });
  }, [layerVisibility, layerLocked, layerCollapsed, objectViewState, updateSettings]);

  // Persist lock state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(LOCKED_STORAGE_KEY, JSON.stringify(layerLocked));
    } catch (error) {
      console.error('Failed to save layer locked state to localStorage:', error);
    }
  }, [layerLocked]);

  // Persist visibility to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(layerVisibility));
    } catch (error) {
      console.error('Failed to save layer visibility to localStorage:', error);
    }
  }, [layerVisibility]);

  // Persist collapsed state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(layerCollapsed));
    } catch (error) {
      console.error('Failed to save layer collapsed state to localStorage:', error);
    }
  }, [layerCollapsed]);

  /**
   * Toggle layer visibility
   */
  const toggleLayerVisibility = (layerId) => {
    setLayerVisibilityState((prev) => ({ ...prev, [layerId]: !prev[layerId] }));
    pendingPersistRef.current = true;
  };

  /**
   * Set layer visibility explicitly
   */
  const setLayerVisibility = (layerId, visible) => {
    setLayerVisibilityState((prev) => ({ ...prev, [layerId]: visible }));
    pendingPersistRef.current = true;
  };

  /**
   * Toggle layer collapsed state
   */
  const toggleLayerCollapsed = (layerId) => {
    setLayerCollapsedState((prev) => ({ ...prev, [layerId]: !prev[layerId] }));
    pendingPersistRef.current = true;
  };

  /**
   * Set layer collapsed state explicitly
   */
  const setLayerCollapsed = (layerId, collapsed) => {
    setLayerCollapsedState((prev) => ({ ...prev, [layerId]: collapsed }));
    pendingPersistRef.current = true;
  };

  /**
   * Check if layer is visible
   */
  const isLayerVisible = (layerId) => {
    return layerVisibility[layerId] !== false;
  };

  /**
   * Check if layer is collapsed
   */
  const isLayerCollapsed = (layerId) => {
    return layerCollapsed[layerId] !== false;
  };

  /**
   * Get layer for object (delegates to layerDefinitions)
   */
  const getLayerForObject = (fabricObject) => {
    return getLayerForObjectFromDefinitions(fabricObject);
  };

  /**
   * Get objects grouped by layer
   *
   * @param {fabric.Canvas} canvas - Fabric.js canvas instance
   * @returns {Object} Object with layer IDs as keys and arrays of objects as values
   */
  const getObjectsPerLayer = (canvas) => {
    if (!canvas) return {};

    const objectsByLayer = {};

    // Initialize all layers
    LAYER_DEFINITIONS.forEach((layer) => {
      objectsByLayer[layer.id] = [];
    });

    // Group objects by layer
    canvas.getObjects().forEach((obj) => {
      // Skip grid lines and snap guides
      if (obj.data?.isGridLine || obj.data?.isSnapGuide) return;

      const layerId = getLayerForObject(obj);
      if (objectsByLayer[layerId]) {
        objectsByLayer[layerId].push(obj);
      }
    });

    return objectsByLayer;
  };

  /**
   * Get object count per layer
   *
   * @param {fabric.Canvas} canvas - Fabric.js canvas instance
   * @returns {Object} Object with layer IDs as keys and counts as values
   */
  const getObjectCountPerLayer = (canvas) => {
    const objectsByLayer = getObjectsPerLayer(canvas);
    const counts = {};

    Object.keys(objectsByLayer).forEach((layerId) => {
      counts[layerId] = objectsByLayer[layerId].length;
    });

    return counts;
  };

  /**
   * Get display name for an object (for layer panel object list)
   *
   * Priority:
   * 1. data.gardenObjectProperties.name (user-given name)
   * 2. Garden object type name (e.g., "Hochbeet")
   * 3. Plant common_name
   * 4. Wizard type label
   * 5. Fabric.js type as fallback
   *
   * @param {Object} fabricObject - Fabric.js object
   * @returns {string} Display name
   */
  const getObjectDisplayName = (fabricObject) => {
    if (!fabricObject) return 'Unbekannt';

    const data = fabricObject.data || {};

    // 1. PlantCircle objects (have plantName property or plantProperties.common_name)
    if (fabricObject.objectType === 'plant') {
      if (fabricObject.plantName) {
        return fabricObject.plantName;
      }
      if (data.plantProperties?.common_name) {
        return data.plantProperties.common_name;
      }
    }

    // 2. User-given name for garden objects (check both locations)
    // DynamicGardenObjectProperties stores directly in data.name
    // Older code might store in data.gardenObjectProperties.name
    const userName = data.name || data.gardenObjectProperties?.name;
    if (userName && userName.trim()) {
      return userName;
    }

    // 3. Garden object type name
    if (data.gardenObjectType || data.objectTypeId) {
      return getObjectTypeName(data.gardenObjectType || data.objectTypeId);
    }

    // 4. Wizard type label
    if (data.type) {
      const wizardTypeLabels = {
        'wizard-background': 'Hintergrundbild',
        'height-zone': 'Höhenzone',
        'height-zone-label': 'Höhen-Label',
        'garden-boundary': 'Garten-Umriss',
      };
      if (wizardTypeLabels[data.type]) {
        return wizardTypeLabels[data.type];
      }
    }

    // 5. Fabric.js type fallback
    const fabricType = fabricObject.type || 'object';
    const typeLabels = {
      rect: 'Rechteck',
      circle: 'Kreis',
      polygon: 'Polygon',
      line: 'Linie',
      path: 'Pfad',
      group: 'Gruppe',
    };
    return typeLabels[fabricType] || fabricType;
  };

  /**
   * Toggle layer lock
   */
  const toggleLayerLock = (layerId) => {
    setLayerLockedState((prev) => ({ ...prev, [layerId]: !prev[layerId] }));
    pendingPersistRef.current = true;
  };

  /**
   * Set layer lock explicitly
   */
  const setLayerLock = (layerId, locked) => {
    setLayerLockedState((prev) => ({ ...prev, [layerId]: locked }));
    pendingPersistRef.current = true;
  };

  /**
   * Check if layer is locked
   */
  const isLayerLocked = (layerId) => {
    return layerLocked[layerId] === true;
  };

  /**
   * Reset all layer lock states to default (all unlocked)
   */
  const resetLayerLocked = () => {
    setLayerLockedState(DEFAULT_LOCKED);
    pendingPersistRef.current = true;
  };

  /**
   * Reset all layer visibility to default (all visible)
   */
  const resetLayerVisibility = () => {
    setLayerVisibilityState(DEFAULT_VISIBILITY);
    pendingPersistRef.current = true;
  };

  /**
   * Reset all layer collapsed states to default (all collapsed)
   */
  const resetLayerCollapsed = () => {
    setLayerCollapsedState(DEFAULT_COLLAPSED);
    pendingPersistRef.current = true;
  };

  // ==========================================================================
  // Per-object hide/lock (#760)
  //
  // State lives in the objectViewState map, keyed by a STABLE key: deterministic
  // for the wizard objects that are rebuilt from settings on every load
  // (background, outline, height zones), and data.objectId for user content
  // (plants, beds, structures — their objectId round-trips through canvas_objects).
  // Reads never mutate; a write assigns a data.objectId lazily where needed.
  // ==========================================================================

  /** Deterministic view-state key for objects rebuilt from settings, else null. */
  const wizardViewStateKey = (data) => {
    if (!data) return null;
    if (data.type === 'wizard-background' || data.type === 'garden-boundary') {
      return data.type;
    }
    if (data.type === 'height-zone' || data.type === 'height-zone-label') {
      return `${data.type}:${data.zoneId ?? ''}`;
    }
    return null;
  };

  /** Read-only key: may be null when a user object has no objectId yet. */
  const readViewStateKey = (fabricObject) => {
    const data = fabricObject?.data;
    if (!data) return null;
    return wizardViewStateKey(data) || data.objectId || null;
  };

  /** Write key: assigns a stable data.objectId to user content if missing. */
  const writeViewStateKey = (fabricObject) => {
    const data = fabricObject.data || (fabricObject.data = {});
    return wizardViewStateKey(data) || ensureObjectId(fabricObject);
  };

  /** Merge a flag into an object's entry, dropping empty entries to stay lean. */
  const withViewStateFlag = (prev, key, flag, value) => {
    const nextEntry = { ...prev[key] };
    if (value) nextEntry[flag] = true;
    else delete nextEntry[flag];
    const next = { ...prev };
    if (nextEntry.hidden || nextEntry.locked) next[key] = nextEntry;
    else delete next[key];
    return next;
  };

  /** Toggle an individual object's hidden state. */
  const toggleObjectHidden = (fabricObject) => {
    if (!fabricObject) return;
    const key = writeViewStateKey(fabricObject);
    setObjectViewState((prev) => withViewStateFlag(prev, key, 'hidden', !prev[key]?.hidden));
    pendingPersistRef.current = true;
  };

  /** True when the given object is individually hidden. */
  const isObjectHidden = (fabricObject) => {
    const key = readViewStateKey(fabricObject);
    return Boolean(key && objectViewState[key]?.hidden);
  };

  /** Toggle an individual object's lock state. */
  const toggleObjectLocked = (fabricObject) => {
    if (!fabricObject) return;
    const key = writeViewStateKey(fabricObject);
    setObjectViewState((prev) => withViewStateFlag(prev, key, 'locked', !prev[key]?.locked));
    pendingPersistRef.current = true;
  };

  /** True when the given object is individually locked. */
  const isObjectLocked = (fabricObject) => {
    const key = readViewStateKey(fabricObject);
    return Boolean(key && objectViewState[key]?.locked);
  };

  /** True when every object in the collection is hidden (e.g. height zones). */
  const areObjectsHidden = (fabricObjects) => {
    if (!fabricObjects?.length) return false;
    return fabricObjects.every((obj) => isObjectHidden(obj));
  };

  /**
   * Toggle a whole collection's visibility as one unit: if all are currently
   * hidden, show all; otherwise hide all. Used by the height-zone collection row.
   */
  const toggleObjectsHidden = (fabricObjects) => {
    if (!fabricObjects?.length) return;
    const allHidden = fabricObjects.every((obj) => isObjectHidden(obj));
    const keys = fabricObjects.map((obj) => writeViewStateKey(obj));
    setObjectViewState((prev) => {
      let next = prev;
      keys.forEach((key) => {
        next = withViewStateFlag(next, key, 'hidden', !allHidden);
      });
      return next;
    });
    pendingPersistRef.current = true;
  };

  /**
   * Remove the wizard background image from the garden (#766).
   *
   * The background is a settings-derived object: GardenCanvas rebuilds it from
   * settings.backgroundImage on every load, and its wizard-reload effect drops
   * the live canvas object as soon as that field goes null. So clearing the
   * setting is the whole removal — no direct canvas mutation needed here. The
   * garden's measurements/terrain are already computed and stored in settings,
   * so removing the image after setup triggers no re-measurement (safe).
   *
   * We also drop any leftover per-object view state keyed to the background so a
   * later wizard re-upload starts fresh instead of coming back silently
   * hidden/locked. The write-back effect persists the trimmed map with settings.
   */
  const removeBackgroundImage = () => {
    if (updateSettings) {
      updateSettings({ backgroundImage: null });
    }
    setObjectViewState((prev) => {
      if (!prev['wizard-background']) return prev;
      const next = { ...prev };
      delete next['wizard-background'];
      return next;
    });
    pendingPersistRef.current = true;
  };

  const value = {
    // State
    layerVisibility,
    layerCollapsed,
    layerLocked,
    objectViewState,

    // Visibility actions
    toggleLayerVisibility,
    setLayerVisibility,
    isLayerVisible,
    resetLayerVisibility,

    // Lock actions
    toggleLayerLock,
    setLayerLock,
    isLayerLocked,
    resetLayerLocked,

    // Collapsed state actions
    toggleLayerCollapsed,
    setLayerCollapsed,
    isLayerCollapsed,
    resetLayerCollapsed,

    // Object helpers
    getLayerForObject,
    getObjectsPerLayer,
    getObjectCountPerLayer,
    getObjectDisplayName,

    // Per-object hide/lock actions (#760)
    toggleObjectHidden,
    isObjectHidden,
    toggleObjectLocked,
    isObjectLocked,
    areObjectsHidden,
    toggleObjectsHidden,

    // Background image removal (#766)
    removeBackgroundImage,
  };

  return <LayerContext.Provider value={value}>{children}</LayerContext.Provider>;
};

/**
 * useLayer - Hook to access layer context
 *
 * @returns {Object} Layer context
 * @throws {Error} If used outside of LayerProvider
 *
 * @example
 * const { layerVisibility, toggleLayerVisibility, getObjectCountPerLayer } = useLayer();
 */
export const useLayer = () => {
  const context = useContext(LayerContext);
  if (!context) {
    throw new Error('useLayer must be used within a LayerProvider');
  }
  return context;
};
